import * as React from 'react'
import { CircularProgress } from '@mui/material'
import { makeContentServicePostRequest } from 'infrastructure/contentService'
import { objectValueSort } from 'shared/utils'
import {
  appendRowsToSpreadsheet,
  updateRowsInSpreadsheet,
  createSpreadsheet,
  setSpreadsheetWrapStrategy,
  findSpreadsheetByName,
  WrapStrategy,
} from 'infrastructure/googleDrive'
import { KiddomStandard } from './types'

export const renderLoading = (
  message: string = 'Loading',
): React.JSX.Element => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <CircularProgress size={16} />
      <span style={{ fontStyle: 'italic' }}>{message}</span>
    </div>
  )
}

export const renderMessage = (message: string): React.JSX.Element => {
  return <div style={{ fontStyle: 'italic' }}>{message}</div>
}

/**
 * Process weighted skills lists using the job flow API
 * @param parameters - Parameters for processing weighted skills lists
 * @returns Flow ID for monitoring the job
 */
export async function processWeightedSkillsListsFlow(parameters: {
  tree_id: string
  version_id: string
  weighted_skills_list_nodes: string[]
}): Promise<string> {
  // Create payload with each weighted_skills_list node ID mapping to type change
  const payload: { [key: string]: { type: string } } = {}
  parameters.weighted_skills_list_nodes.forEach((nodeId) => {
    payload[nodeId] = {
      type: 'skills_list',
    }
  })

  const response = await makeContentServicePostRequest(
    `content/v1/kiddom/tree/${parameters.tree_id}:${parameters.version_id}/update-nodes/job/start`,
    payload,
  )

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(
      `Process weighted skills lists flow creation failed with status ${response.status}: ${errorText}`,
    )
  }

  const data = await response.json()
  if (!data.data?.flow_id) {
    throw new Error(
      'No flow_id returned from process weighted skills lists flow',
    )
  }

  return data.data.flow_id
}

/**
 * Get sort order for a grade name
 * Pre Kindergarten = -1, Kindergarten = 0, Grade 1 = 1, Grade 2 = 2, etc.
 */
function getGradeSortOrder(gradeName: string): number {
  const gradeLower = gradeName.toLowerCase().trim()

  // Pre-Kindergarten comes first (exact match: "Pre-Kindergarten")
  if (gradeLower === 'pre-kindergarten') {
    return -1
  }

  // Kindergarten comes next (exact match: "Kindergarten")
  if (gradeLower === 'kindergarten') {
    return 0
  }

  // Extract number from "Grade 1", "Grade 2", etc.
  const gradeMatch =
    gradeLower.match(/grade\s*(\d+)/i) || gradeLower.match(/(\d+)/)
  if (gradeMatch) {
    return parseInt(gradeMatch[1], 10)
  }

  // If no number found, put it at the end
  return 999
}

/**
 * Export Kiddom standards to a Google Spreadsheet
 * @param standards - Array of Kiddom standards to export
 * @param state - State abbreviation (e.g., 'CA')
 * @param subjectName - Subject name (e.g., 'Math')
 * @param accessToken - Google OAuth access token
 * @returns Promise<string> - The spreadsheet ID
 */
export async function exportStandardsToSpreadsheet(
  standards: KiddomStandard[],
  state: string,
  subjectName: string,
  accessToken: string,
): Promise<string> {
  const headers = [
    'id',
    'state',
    'name',
    'description',
    'subject',
    'grade',
    'adoption_date',
  ]

  // Sort standards by grade (Pre-Kindergarten, Kindergarten, Grade 1, Grade 2, etc.)
  const sortedStandards = [...standards]
    .sort(objectValueSort((standard) => getGradeSortOrder(standard.grade_name)))
    .reverse()

  const rows = sortedStandards.map((standard) => [
    standard.standard_id.toString(),
    standard.state || '(not set)',
    standard.standard_name || '(not set)',
    standard.standard_description || '(not set)',
    standard.subject_name || '(not set)',
    standard.grade_name || '(not set)',
    standard.adoption_date === 0 || !standard.adoption_date
      ? '(not set)'
      : standard.adoption_date.toString(),
  ])

  const spreadsheetName = `Kiddom_Standards_${state}_${subjectName.replace(
    /\s+/g,
    '_',
  )}`

  // Find existing spreadsheet with the same name
  let spreadsheetId: string
  try {
    const existingSpreadsheetIds = await findSpreadsheetByName(
      accessToken,
      spreadsheetName,
    )

    if (existingSpreadsheetIds.length > 0) {
      // Reuse existing spreadsheet - clear its contents and update with new data
      spreadsheetId = existingSpreadsheetIds[0]
      // Use updateRowsInSpreadsheet which clears and replaces all data
      const updateResponse = await updateRowsInSpreadsheet(
        accessToken,
        spreadsheetId,
        [headers, ...rows],
      )
      // Ensure the update completed successfully
      if (!updateResponse.ok) {
        throw new Error(
          `Failed to update spreadsheet: ${updateResponse.statusText}`,
        )
      }
    } else {
      // Create new spreadsheet and append data
      spreadsheetId = await createSpreadsheet(accessToken, spreadsheetName)
      const appendResponse = await appendRowsToSpreadsheet(
        accessToken,
        spreadsheetId,
        [headers, ...rows],
      )
      // Ensure the append completed successfully
      if (!appendResponse.ok) {
        throw new Error(
          `Failed to append to spreadsheet: ${appendResponse.statusText}`,
        )
      }
    }
  } catch (error) {
    // If search fails, just create a new spreadsheet
    spreadsheetId = await createSpreadsheet(accessToken, spreadsheetName)
    const appendResponse = await appendRowsToSpreadsheet(
      accessToken,
      spreadsheetId,
      [headers, ...rows],
    )
    if (!appendResponse.ok) {
      throw new Error(
        `Failed to append to spreadsheet: ${appendResponse.statusText}`,
      )
    }
  }
  const wrapResponse = await setSpreadsheetWrapStrategy(
    accessToken,
    spreadsheetId,
    WrapStrategy.CLIP,
  )
  if (!wrapResponse.ok) {
    throw new Error(
      `Failed to set spreadsheet wrap strategy: ${wrapResponse.statusText}`,
    )
  }

  // Format header row to be bold and italic
  const formatResponse = await formatHeaderRow(
    accessToken,
    spreadsheetId,
    headers.length,
  )
  if (!formatResponse.ok) {
    throw new Error(`Failed to format header row: ${formatResponse.statusText}`)
  }

  return spreadsheetId
}

/**
 * Formats the header row (row 0) to be bold and italic
 */
async function formatHeaderRow(
  accessToken: string,
  spreadsheetId: string,
  columnCount: number,
): Promise<Response> {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`

  const requestBody = {
    requests: [
      {
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: 0,
            endRowIndex: 1,
            startColumnIndex: 0,
            endColumnIndex: columnCount,
          },
          cell: {
            userEnteredFormat: {
              textFormat: {
                bold: true,
                italic: true,
              },
            },
          },
          fields: 'userEnteredFormat.textFormat',
        },
      },
    ],
  }

  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })
}
